import React, { useRef } from 'react';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import type { ModuleId, ReportData } from '../data/types';
import { MODULES, MODULE_META_EXTRA } from '../modules/moduleRegistry';
import { ScreenCover, ScreenPage } from '../primitives/ScreenPrimitives';
import { ScreenToc, ScreenDisclaimer, type TocEntry } from '../primitives/staticPages';
import { RenderTargetProvider } from '../primitives/RenderTarget';
import { ScreenDirectorDetail } from '../modules/Management';
import { fmtDate } from '../modules/formatUtils';
import transitionStyles from './SectionTransition.module.scss';

export type ReportPreviewProps = {
    data: ReportData;
    enabledModules: ModuleId[];
    generatedOn?: string;
    pnoLogoUrl?: string;
};

export const ReportPreview: React.FC<ReportPreviewProps> = ({ data, enabledModules, generatedOn, pnoLogoUrl }) => {
    const today = generatedOn ?? fmtDate(new Date().toISOString());
    const meta: Array<{ label: string; value: string }> = [];
    if (data.identity.cin && data.identity.cin !== '—') meta.push({ label: 'CIN', value: data.identity.cin });
    if (data.identity.pan && data.identity.pan !== '—') meta.push({ label: 'PAN', value: data.identity.pan });
    if (data.identity.sector && data.identity.sector !== '—') meta.push({ label: 'Sector', value: data.identity.sector });
    if (data.identity.state) meta.push({ label: 'HQ', value: data.identity.state });
    if (data.identity.foundationDate) meta.push({ label: 'Founded', value: data.identity.foundationDate });
    if (data.score?.companyScore && Number.isFinite(data.score.companyScore) && data.score.companyScore > 0) {
        const scaled = (data.score.companyScore / 4) * 10;
        meta.push({ label: 'AICA Risk Score', value: `${scaled.toFixed(1)} / 10` });
    }

    const renderable = enabledModules.map((id) => MODULES[id as keyof typeof MODULES]).filter((m) => m && m.hasData(data));

    const tocEntries: TocEntry[] = renderable.map((m, i) => ({
        index: String(i + 1).padStart(2, '0'),
        title: m.meta.title,
        subtitle: m.meta.description,
        coverage: MODULE_META_EXTRA[m.meta.id]?.coverage,
    }));

    return (
        <div>
            <ScreenCover
                orgName={data.identity.orgName}
                subtitle={[data.identity.sector, data.identity.state].filter(Boolean).join(' · ')}
                meta={meta}
                generatedOn={today}
                pnoLogoUrl={pnoLogoUrl}
            />
            {tocEntries.length > 0 ? <ScreenToc orgName={data.identity.orgName} entries={tocEntries} /> : null}
            <TransitionGroup component={null}>
                {renderable.map((m, i) => {
                    const index = String(i + 1).padStart(2, '0');
                    // When the management module renders, also emit one extra
                    // ScreenPage per director immediately after — mirrors the
                    // PDF behaviour in ReportDocument so preview and export
                    // visually agree.
                    const isManagement = m.meta.id === 'management';
                    const directors = isManagement && data.management ? data.management.profiles : [];
                    return (
                        <CSSTransition
                            key={m.meta.id}
                            timeout={420}
                            classNames={{
                                enter: transitionStyles.enter,
                                enterActive: transitionStyles.enterActive,
                                enterDone: transitionStyles.enterDone,
                                exit: transitionStyles.exit,
                                exitActive: transitionStyles.exitActive,
                            }}
                        >
                            <div>
                                <SectionWrapper
                                    orgName={data.identity.orgName}
                                    sectionLabel={`${index} · ${m.meta.title}`}
                                    pageNumber={i + 1}
                                    totalPages={renderable.length}
                                >
                                    <RenderTargetProvider target="screen">
                                        <m.Screen data={data} index={index} />
                                    </RenderTargetProvider>
                                </SectionWrapper>
                                {directors.map((profile, di) => (
                                    <SectionWrapper
                                        key={`director-${di}`}
                                        orgName={data.identity.orgName}
                                        sectionLabel={`${index} · Director Detail`}
                                        pageNumber={i + 1}
                                        totalPages={renderable.length}
                                    >
                                        <RenderTargetProvider target="screen">
                                            <ScreenDirectorDetail
                                                profile={profile}
                                                moduleIndex={index}
                                                directorIndex={di + 1}
                                                directorTotal={directors.length}
                                                data={data}
                                            />
                                        </RenderTargetProvider>
                                    </SectionWrapper>
                                ))}
                            </div>
                        </CSSTransition>
                    );
                })}
            </TransitionGroup>
            <ScreenPage
                orgName={data.identity.orgName}
                sectionLabel="Disclaimer"
                pageNumber={renderable.length + 1}
                totalPages={renderable.length + 1}
            >
                <ScreenDisclaimer />
            </ScreenPage>
        </div>
    );
};

const SectionWrapper = React.forwardRef<
    HTMLDivElement,
    {
        children: React.ReactNode;
        orgName: string;
        sectionLabel: string;
        pageNumber: number;
        totalPages: number;
    }
>(({ children, orgName, sectionLabel, pageNumber, totalPages }, ref) => {
    const innerRef = useRef<HTMLDivElement | null>(null);
    return (
        <div
            ref={(node) => {
                innerRef.current = node;
                if (typeof ref === 'function') ref(node);
                else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
            }}
            className={transitionStyles.SectionSlot}
        >
            <ScreenPage orgName={orgName} sectionLabel={sectionLabel} pageNumber={pageNumber} totalPages={totalPages}>
                {children}
            </ScreenPage>
        </div>
    );
});
SectionWrapper.displayName = 'SectionWrapper';
